import parsePhoneNumberFromString from 'libphonenumber-js'
import { getAgentCountry } from 'v2/api-console/shared/utils/date-utils'
import dayjs from 'v2/utils/dayjs'

/**
 * Normalize a phone-number filter to the digits cdr-api exact-matches (E.164
 * without '+'). An explicit +country code is honored; a bare national number
 * gets the org's country; unparseable input falls back to digits-only. Returns
 * '' when there are no usable digits — the caller must then DROP the filter, not
 * send an empty one. Shared by the Zentrunk logs list query and its export so
 * the two paths normalize identically (a formatted number must match the same
 * rows it exports).
 */
export const toCdrPhoneNumber = (raw: string): string => {
  const trimmed = raw.trim()
  const parsed = parsePhoneNumberFromString(
    trimmed,
    trimmed.startsWith('+') ? undefined : getAgentCountry(),
  )
  return parsed?.isValid() ? parsed.number.replace('+', '') : trimmed.replace(/\D/g, '')
}

/**
 * Shared filter transforms for the Recordings page.
 *
 * Both the list query (`listCallRecordings`) and the destructive export / bulk-delete
 * (`buildBackendFilters`) must turn the same raw URL filters into backend params the
 * SAME way — otherwise "Delete all matching filters" acts on a different set than the
 * rows shown. These helpers are the single source of truth so the two paths can't drift.
 */

// "Any" sentinels for the storage-duration range — the value picked when the user wants
// no lower/upper bound. Stripped before hitting the backend. Single source of truth;
// recordings/constants.ts re-exports these for the filter UI.
export const STORAGE_DURATION_ANY_MIN = '-1'
export const STORAGE_DURATION_ANY_MAX = '946080000'

const BACKEND_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/**
 * Normalize a phone-number filter to the digits the backend exact-matches (e.g.
 * "+1 (415) 555-0000" -> "14155550000"). Normalization is applied ONLY when the value parses
 * as a valid phone number (an explicit +country code is honored; a bare national number gets
 * the org's country — same rule as {@link toCdrPhoneNumber}); anything else is a SIP id /
 * endpoint alias (e.g. "sip:alice@example.com", or a numeric-dotted alias like "123.456") and
 * is matched exactly as typed. Returns undefined when there's nothing usable to filter on
 * (empty, whitespace-only, or punctuation with no alphanumeric content) so the caller can drop
 * the param entirely.
 */
export const sanitizePhoneParam = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = parsePhoneNumberFromString(
    trimmed,
    trimmed.startsWith('+') ? undefined : getAgentCountry(),
  )
  if (parsed?.isValid()) return parsed.number.replace('+', '')
  // Not a valid phone number → treat as a SIP id / alias and match it as typed, unless it's
  // just punctuation with no alphanumeric content (then there's nothing to filter on).
  return /[a-z0-9]/i.test(trimmed) ? trimmed : undefined
}

/**
 * Split a "min,max" storage-duration filter (in days) into its bounds, dropping the
 * "Any" sentinels. Returns only the bounds that are real limits.
 */
export const durationRangeToBounds = (raw: unknown): { gte?: string; lte?: string } => {
  if (typeof raw !== 'string' || !raw) return {}
  const [min, max] = raw.split(',')
  const bounds: { gte?: string; lte?: string } = {}
  if (min && min !== STORAGE_DURATION_ANY_MIN) bounds.gte = min
  if (max && max !== STORAGE_DURATION_ANY_MAX) bounds.lte = max
  return bounds
}

/**
 * Convert a "start,end" date-range filter (dates picked in the org/agent timezone) into
 * UTC bounds for the backend. The upper bound is capped to "now" when it's in the future.
 * A bound is only emitted when its date is present AND parses to a valid date — so an
 * empty/partial/malformed value yields `{}` rather than a literal "Invalid Date" reaching
 * the API (which feeds list filtering and the destructive bulk-delete).
 */
export const dateRangeToUtc = (raw: string, tz: string): { gte?: string; lte?: string } => {
  const [startDate, endDate] = raw.split(',')
  const out: { gte?: string; lte?: string } = {}

  if (startDate) {
    const gte = dayjs.tz(`${startDate.slice(0, 10)} 00:00:00`, tz)
    if (gte.isValid()) out.gte = gte.utc().format(BACKEND_DATE_FORMAT)
  }

  const end = endDate || startDate
  if (end) {
    let lte = dayjs.tz(`${end.slice(0, 10)} 23:59:59`, tz)
    if (lte.isValid()) {
      const now = dayjs()
      if (lte.isAfter(now)) lte = now
      out.lte = lte.utc().format(BACKEND_DATE_FORMAT)
    }
  }

  return out
}

/**
 * ±1 day `add_time` range ("start,end") around a call's end time, or undefined when there is
 * no usable date. The Recording API silently applies its own ~7-day window when given no
 * add_time and 400s on spans beyond ~30 days, so reaching an older recording means aiming a
 * narrow window at the call. The ±1 day absorbs the gap between the account timezone a CDR
 * end_time carries and the agent timezone {@link dateRangeToUtc} converts from.
 */
export const recordingDayWindow = (endTime?: string | null): string | undefined => {
  if (!endTime) return undefined
  const end = dayjs(endTime)
  if (!end.isValid()) return undefined
  return `${end.subtract(1, 'day').format('YYYY-MM-DD')},${end.add(1, 'day').format('YYYY-MM-DD')}`
}
